import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const CurrencyCtx = createContext(null);

// Country → default currency mapping
// RDC uses USD as everyday currency for medium/high-value retail (imports, wine, appliances...)
// while CDF is reserved for small daily purchases. Users can still switch to CDF via the currency selector.
export const COUNTRIES = [
  { code: "FR", name: "France", flag: "🇫🇷", currency: "EUR" },
  { code: "CD", name: "RDC / Congo", flag: "🇨🇩", currency: "USD" },
  { code: "US", name: "International", flag: "🌍", currency: "USD" },
];

export const CURRENCY_META = {
  EUR: { symbol: "€", position: "after", decimals: 2, code: "EUR" },
  USD: { symbol: "$", position: "before", decimals: 2, code: "USD" },
  CDF: { symbol: "FC", position: "after", decimals: 0, code: "CDF" },
};

export function CurrencyProvider({ children }) {
  const [country, setCountry] = useState(() => localStorage.getItem("sukana_country") || "FR");
  const [currency, setCurrency] = useState(() => localStorage.getItem("sukana_currency") || "EUR");
  const [rates, setRates] = useState({ base: "EUR", rates: { EUR: 1, USD: 1.08, CDF: 2900 } });

  useEffect(() => {
    api.get("/settings/currency").then((r) => setRates(r.data)).catch(() => {});
  }, []);

  useEffect(() => { localStorage.setItem("sukana_country", country); }, [country]);
  useEffect(() => { localStorage.setItem("sukana_currency", currency); }, [currency]);

  // Convert an amount known in EUR (pivot) to the target currency
  const convertFromEUR = useCallback((amountEUR, target = currency) => {
    const r = rates.rates?.[target];
    if (!r) return amountEUR;
    return amountEUR * r;
  }, [rates, currency]);

  // Given a product with prices dict + legacy price, return the display price in current currency
  const getDisplayPrice = useCallback((item) => {
    if (!item) return 0;
    // Prefer explicit price in target currency
    if (item.prices && item.prices[currency] != null) return item.prices[currency];
    // Else convert from EUR pivot using rates
    const eur = item.price ?? (item.prices?.EUR ?? 0);
    return convertFromEUR(eur, currency);
  }, [currency, convertFromEUR]);

  const format = useCallback((amount, cur = currency) => {
    const meta = CURRENCY_META[cur] || CURRENCY_META.EUR;
    const n = Number(amount || 0).toLocaleString("fr-FR", { minimumFractionDigits: meta.decimals, maximumFractionDigits: meta.decimals });
    return meta.position === "before" ? `${meta.symbol}${n}` : `${n} ${meta.symbol}`;
  }, [currency]);

  const selectCountry = useCallback((code) => {
    const c = COUNTRIES.find((x) => x.code === code);
    if (c) {
      setCountry(code);
      setCurrency(c.currency);
    }
  }, []);

  const value = useMemo(() => ({
    country, currency, rates, setCountry: selectCountry, setCurrency,
    convertFromEUR, getDisplayPrice, format, refreshRates: () => api.get("/settings/currency").then((r) => setRates(r.data)),
  }), [country, currency, rates, selectCountry, convertFromEUR, getDisplayPrice, format]);

  return <CurrencyCtx.Provider value={value}>{children}</CurrencyCtx.Provider>;
}

export const useCurrency = () => useContext(CurrencyCtx);
